import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { HiBars3 } from "react-icons/hi2";
import { Field, FormInput, FormSelect, FormSwitch, FormTextarea, CollapsibleSection, ItemCard, ItemHeader, AddButton, Divider, SubsectionTitle, Grid2, isFieldInvalid, isFieldWarning } from "@/components/jobOffer/ui";
import { CHANNEL_OPTIONS } from "@/components/campaign/channelOptions";
import { LEAD_STATUS_OPTIONS } from "@/components/campaign/leadStatusOptions";
import { createEmptyStatusRule } from "@/models/campaign";

const CHANNEL_LABELS = Object.fromEntries(CHANNEL_OPTIONS.map((o) => [o.value, o.label]));
const POSITION_LABELS = ["Podstawowy", "Fallback 1", "Fallback 2"];

const SortableChannelRow = ({ channel, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: channel });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : "auto",
    };
    return (
        <HStack ref={setNodeRef} style={style} borderWidth={1} borderColor="border.primary" borderRadius={8} p={3} justify="space-between" bg="white">
            <HStack gap={3}>
                <Box w={5} h={5} borderRadius="full" bg="purple.900" color="purple.400" fontSize="xs" display="flex" alignItems="center" justifyContent="center" fontWeight={500}>
                    {index + 1}
                </Box>
                <Text fontSize="sm" color="text.white">{CHANNEL_LABELS[channel] ?? channel}</Text>
                <Text fontSize="10px" px={2} py={0.5} borderRadius={6} bg="bg.highlight" color="text.grey">
                    {POSITION_LABELS[index] ?? `Fallback ${index}`}
                </Text>
            </HStack>
            <Box
                {...attributes} {...listeners}
                cursor="grab" _active={{ cursor: "grabbing" }}
                color="text.grey" display="flex" p={1.5} borderRadius={6}
                _hover={{ bg: "gray.100", color: "gray.700" }}
                transition="background 0.15s ease, color 0.15s ease"
                aria-label="Przeciągnij, żeby zmienić kolejność"
            >
                <HiBars3 size={18} />
            </Box>
        </HStack>
    );
};

const AGENT_ACTION_OPTIONS = [
    { value: "Rozpocznij pierwszy kontakt sprzedażowy", label: "Rozpocznij pierwszy kontakt sprzedażowy" },
    { value: "Wyślij przypomnienie", label: "Wyślij przypomnienie" },
    { value: "Kontynuuj rozmowę / follow-up", label: "Kontynuuj rozmowę / follow-up" },
    { value: "Przekaż do człowieka", label: "Przekaż do człowieka" },
    { value: "Zakończ / archiwizuj", label: "Zakończ / archiwizuj" },
];

const Step5ChannelsAndRules = ({ formik, values, setValues, warningErrors, visited, forceOpen }) => {
    const set = (key, val) => setValues({ ...values, [key]: val });

    const channelsInvalid = isFieldInvalid(formik, "communicationChannels");
    const channelsWarning = !channelsInvalid && isFieldWarning(warningErrors, visited, "communicationChannels");

    const rules = values.statusAutomationRules ?? [];
    const setRules = (next) => set("statusAutomationRules", next);
    const updateRule = (i, patch) => setRules(rules.map((r, idx) => idx === i ? { ...r, ...patch } : r));
    const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));
    const addRule = () => setRules([...rules, createEmptyStatusRule()]);

    const cc = values.communicationChannels ?? {};
    const setChannels = (patch) => set("communicationChannels", { ...cc, ...patch });
    const channelOrder = cc.channelOrder?.length ? cc.channelOrder : CHANNEL_OPTIONS.map((o) => o.value);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    const handleChannelDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = channelOrder.indexOf(active.id);
        const newIndex = channelOrder.indexOf(over.id);
        setChannels({ channelOrder: arrayMove(channelOrder, oldIndex, newIndex) });
    };
    const setWhatsApp = (patch) => setChannels({ whatsApp: { ...cc.whatsApp, ...patch } });
    const setSms = (patch) => setChannels({ sms: { ...cc.sms, ...patch } });
    const setEmail = (patch) => setChannels({ email: { ...cc.email, ...patch } });

    return (
        <VStack w="full" align="stretch" gap={4}>
            <CollapsibleSection
                title="Kanały komunikacji"
                subtitle="Kolejność prób i konfiguracja WhatsApp/SMS/E-mail"
                defaultOpen
                forceOpen={forceOpen}
            >
                <VStack align="stretch" gap={5}>
                    {(channelsInvalid || channelsWarning) && (
                        <Text fontSize="xs" fontWeight={600} color={channelsInvalid ? "red.400" : "orange.500"}>
                            Włącz co najmniej jeden kanał komunikacji poniżej.
                        </Text>
                    )}
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleChannelDragEnd}>
                        <SortableContext items={channelOrder} strategy={verticalListSortingStrategy}>
                            <VStack align="stretch" gap={2}>
                                {channelOrder.map((channel, i) => (
                                    <SortableChannelRow key={channel} channel={channel} index={i} />
                                ))}
                            </VStack>
                        </SortableContext>
                    </DndContext>
                    <Grid2>
                        <Field label="Czas opóźnienia między kanałami (min)">
                            <FormInput
                                type="number" min="0"
                                value={cc.delayBetweenChannelsMinutes ?? ""}
                                onChange={(e) => setChannels({ delayBetweenChannelsMinutes: e.target.value === "" ? null : Number(e.target.value) })}
                            />
                        </Field>
                    </Grid2>
                    <FormSwitch label="Komunikacja równoległa" checked={!!cc.parallelCommunication} onChange={(v) => setChannels({ parallelCommunication: v })} />

                    <Divider my={1} />
                    <SubsectionTitle>WhatsApp</SubsectionTitle>
                    <FormSwitch label="Włącz WhatsApp" checked={!!cc.whatsApp?.enabled} onChange={(v) => setWhatsApp({ enabled: v })} />
                    {cc.whatsApp?.enabled && (
                        <VStack align="stretch" gap={4}>
                            <Grid2>
                                <Field label="Numer nadawcy (WhatsApp Business)">
                                    <FormInput value={cc.whatsApp?.senderNumber ?? ""} onChange={(e) => setWhatsApp({ senderNumber: e.target.value })} />
                                </Field>
                                <Field label="Kanał fallback">
                                    <FormSelect value={cc.whatsApp?.fallbackChannel ?? ""} onChange={(v) => setWhatsApp({ fallbackChannel: v || null })} options={CHANNEL_OPTIONS.filter((o) => o.value !== "WhatsApp")} placeholder="Brak" />
                                </Field>
                            </Grid2>
                            <Field label="Wiadomość startowa WhatsApp" hint="Placeholdery: {imie}, {kampania}, {link}">
                                <FormTextarea value={cc.whatsApp?.startMessageTemplate ?? ""} onChange={(e) => setWhatsApp({ startMessageTemplate: e.target.value })} rows={3} />
                            </Field>
                        </VStack>
                    )}

                    <Divider my={1} />
                    <SubsectionTitle>SMS</SubsectionTitle>
                    <FormSwitch label="Włącz SMS" checked={!!cc.sms?.enabled} onChange={(v) => setSms({ enabled: v })} />
                    {cc.sms?.enabled && (
                        <VStack align="stretch" gap={4}>
                            <Grid2>
                                <Field label="Numer / ID nadawcy SMS">
                                    <FormInput value={cc.sms?.senderIdOrNumber ?? ""} onChange={(e) => setSms({ senderIdOrNumber: e.target.value })} />
                                </Field>
                                <Field label="Fallback po SMS">
                                    <FormSelect value={cc.sms?.fallbackChannel ?? ""} onChange={(v) => setSms({ fallbackChannel: v || null })} options={CHANNEL_OPTIONS.filter((o) => o.value !== "Sms")} placeholder="Brak" />
                                </Field>
                            </Grid2>
                            <Field label="Szablon SMS" hint="Placeholdery: {imie}, {kampania}, {link}">
                                <FormTextarea value={cc.sms?.messageTemplate ?? ""} onChange={(e) => setSms({ messageTemplate: e.target.value })} rows={3} />
                            </Field>
                        </VStack>
                    )}

                    <Divider my={1} />
                    <SubsectionTitle>E-mail</SubsectionTitle>
                    <FormSwitch label="Włącz e-mail" checked={!!cc.email?.enabled} onChange={(v) => setEmail({ enabled: v })} />
                    {cc.email?.enabled && (
                        <VStack align="stretch" gap={4}>
                            <Grid2>
                                <Field label="Adres nadawcy">
                                    <FormInput value={cc.email?.senderAddress ?? ""} onChange={(e) => setEmail({ senderAddress: e.target.value })} />
                                </Field>
                                <Field label="Nazwa nadawcy">
                                    <FormInput value={cc.email?.senderName ?? ""} onChange={(e) => setEmail({ senderName: e.target.value })} />
                                </Field>
                            </Grid2>
                            <Field label="Temat wiadomości">
                                <FormInput value={cc.email?.subject ?? ""} onChange={(e) => setEmail({ subject: e.target.value })} />
                            </Field>
                            <Field label="Treść e-maila" hint="Placeholdery: {imie}, {kampania}, {link}">
                                <FormTextarea value={cc.email?.bodyTemplate ?? ""} onChange={(e) => setEmail({ bodyTemplate: e.target.value })} rows={4} />
                            </Field>
                            <FormSwitch label="Możliwość dołączania załączników" checked={!!cc.email?.allowAttachments} onChange={(v) => setEmail({ allowAttachments: v })} />
                        </VStack>
                    )}
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection title="Reguły statusów" subtitle="Dla każdego statusu leada: jaką akcję ma podjąć Agent i którym kanałem" defaultOpen forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    {rules.map((r, i) => (
                        <ItemCard key={r.id ?? i}>
                            <ItemHeader title={LEAD_STATUS_OPTIONS.find(o => o.value === r.status)?.label ?? "Nowa reguła statusu"} index={i} onRemove={() => removeRule(i)} />
                            <VStack align="stretch" gap={3}>
                                <Field label="Status" required>
                                    <FormSelect value={r.status ?? ""} onChange={(v) => updateRule(i, { status: v || null })} options={LEAD_STATUS_OPTIONS} placeholder="Wybierz status" />
                                </Field>
                                <FormSwitch label="Wyzwala akcję agenta" checked={!!r.isActive} onChange={(v) => updateRule(i, { isActive: v })} />
                                <Field label="Akcja agenta">
                                    <FormSelect value={r.agentAction ?? ""} onChange={(v) => updateRule(i, { agentAction: v || null })} options={AGENT_ACTION_OPTIONS} placeholder="Wybierz akcję" />
                                </Field>
                                <Grid2>
                                    <Field label="Kanał podstawowy">
                                        <FormSelect value={r.primaryChannel ?? ""} onChange={(v) => updateRule(i, { primaryChannel: v || null })} options={CHANNEL_OPTIONS} placeholder="Wybierz kanał" />
                                    </Field>
                                    <Field label="Kanał dodatkowy / fallback">
                                        <FormSelect value={r.fallbackChannel ?? ""} onChange={(v) => updateRule(i, { fallbackChannel: v || null })} options={CHANNEL_OPTIONS} placeholder="Brak" />
                                    </Field>
                                </Grid2>
                                <Field label="Treść komunikatu startowego (opcjonalnie)" hint="Placeholdery: {imie}, {kampania}, {link}">
                                    <FormTextarea value={r.startMessageTemplate ?? ""} onChange={(e) => updateRule(i, { startMessageTemplate: e.target.value })} rows={3} />
                                </Field>
                            </VStack>
                        </ItemCard>
                    ))}
                    {rules.length === 0 && (
                        <Text fontSize="xs" color="text.grey">
                            Dodaj przynajmniej regułę dla statusu "Nowy", żeby Agent wiedział, kiedy i jak zacząć rozmowę.
                        </Text>
                    )}
                    <AddButton label="Dodaj regułę statusu" onClick={addRule} />
                </VStack>
            </CollapsibleSection>
        </VStack>
    );
};

export default Step5ChannelsAndRules;
